import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { Flex, Stack } from "@/components/layout";
import { Text } from "@/components/typography";
import {
  ActionBar,
  Alert,
  Button,
  Card,
  EmptyState,
  Filters,
  Input,
  LoadingState,
  Pagination,
  SectionHeader,
  Select,
  StatusChip,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
} from "@/components/ui";
import { apiErrorMessage } from "@/api/client";
import { useApiClient } from "@/api/useApiClient";
import { usePageBreadcrumbs } from "@/shell/usePageBreadcrumbs";
import { createAnalyticsApi } from "../api";
import {
  classificationTone,
  formatLabel,
  moduleDrilldownPath,
  recordStatusTone,
} from "../labels";
import { useAnalyticsQueue } from "../useAnalyticsQueue";
import type { SavedViewDto, UserSegmentDto } from "../types";

const RECORD_STATUSES = [
  "draft",
  "active",
  "paused",
  "restricted",
  "suspended",
  "archived",
  "closed",
  "retired",
] as const;

const CLASSIFICATIONS = [
  "public",
  "internal",
  "confidential",
  "strictly_confidential",
  "regulated",
] as const;

const HUB_QUEUES = [
  {
    to: "/app/analytics/saved-views",
    title: "Saved views",
    body: "Persisted filter/sort definitions scoped to a module. Drill into the owning HQ module when the module string is recognized. Not a metric dashboard.",
  },
  {
    to: "/app/analytics/segments",
    title: "User segments",
    body: "Saved user segment criteria definitions with classification. Criteria JSON is opaque — not evaluated or attributed here.",
  },
] as const;

/** The "Saved views & segments" tab of the Analytics page. */
export function SavedViewsHubPanel() {
  const navigate = useNavigate();
  const client = useApiClient();
  const api = useMemo(() => createAnalyticsApi(client), [client]);

  const [views, setViews] = useState<SavedViewDto[]>([]);
  const [segments, setSegments] = useState<UserSegmentDto[]>([]);
  const [viewsHasMore, setViewsHasMore] = useState(false);
  const [segmentsHasMore, setSegmentsHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [viewList, segmentList] = await Promise.all([
        api.listSavedViews({
          status: "active",
          limit: 25,
          sort: "-created_at",
        }),
        api.listUserSegments({
          status: "active",
          limit: 25,
          sort: "-created_at",
        }),
      ]);
      setViews(viewList.items);
      setSegments(segmentList.items);
      setViewsHasMore(Boolean(viewList.next_cursor));
      setSegmentsHasMore(Boolean(segmentList.next_cursor));
    } catch (err) {
      setError(apiErrorMessage(err));
      setViews([]);
      setSegments([]);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    void load();
  }, [load]);

  const viewsByModule = useMemo(() => {
    const counts = new Map<string, number>();
    for (const view of views) {
      counts.set(view.module, (counts.get(view.module) ?? 0) + 1);
    }
    return [...counts.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [views]);

  const segmentsByClassification = useMemo(() => {
    const counts = new Map<string, number>();
    for (const segment of segments) {
      counts.set(
        segment.classification,
        (counts.get(segment.classification) ?? 0) + 1,
      );
    }
    return [...counts.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [segments]);

  return (
    <Stack gap={24}>
      <SectionHeader
        title="Saved views and segments"
        description="Filters and audience definitions that staff have saved. A saved view remembers how someone filtered a queue; a segment describes a group of users. Trends, funnels and retention are on the other tabs."
        actions={
          <Button variant="secondary" onClick={() => void load()}>
            Refresh
          </Button>
        }
      />

      {error ? (
        <Alert tone="danger" title="Unable to load Analytics">
          {error}
        </Alert>
      ) : null}

      {loading ? <LoadingState message="Loading Analytics…" /> : null}

      {!loading ? (
        <Flex gap={16} wrap>
          <Card
            header={
              <Text role="title" as="h3">
                Active saved views (this page)
              </Text>
            }
          >
            <Stack gap={8}>
              <Text role="h2" as="p">
                {views.length}
                {viewsHasMore ? "+" : ""}
              </Text>
              <Text role="small" color="secondary" as="p">
                Count of active saved_view rows returned by GET
                /v1/hq/admin/saved-views — not a business KPI.
              </Text>
              <Button
                variant="secondary"
                onClick={() =>
                  void navigate("/app/analytics/saved-views?status=active")
                }
              >
                Open saved views
              </Button>
            </Stack>
          </Card>
          <Card
            header={
              <Text role="title" as="h3">
                Active segments (this page)
              </Text>
            }
          >
            <Stack gap={8}>
              <Text role="h2" as="p">
                {segments.length}
                {segmentsHasMore ? "+" : ""}
              </Text>
              <Text role="small" color="secondary" as="p">
                Count of active user_segment rows returned by GET
                /v1/hq/admin/user-segments — not a reach or conversion metric.
              </Text>
              <Button
                variant="secondary"
                onClick={() =>
                  void navigate("/app/analytics/segments?status=active")
                }
              >
                Open segments
              </Button>
            </Stack>
          </Card>
        </Flex>
      ) : null}

      <Flex gap={16} wrap>
        {HUB_QUEUES.map((queue) => (
          <Card
            key={queue.to}
            header={
              <Text role="title" as="h3">
                {queue.title}
              </Text>
            }
          >
            <Stack gap={12}>
              <Text role="small" color="secondary" as="p">
                {queue.body}
              </Text>
              <Button
                variant="secondary"
                onClick={() => void navigate(queue.to)}
              >
                Open queue
              </Button>
            </Stack>
          </Card>
        ))}
      </Flex>

      {!loading ? (
        <Flex gap={16} wrap>
          <Card
            header={
              <Text role="title" as="h3">
                Breakdown: views by module (this page)
              </Text>
            }
          >
            {viewsByModule.length === 0 ? (
              <EmptyState
                title="No active views on this page"
                reason="none"
              />
            ) : (
              <Stack gap={8}>
                <Text role="caption" color="muted" as="p">
                  Grouping of list results by the module field. Not a market or
                  revenue breakdown.
                </Text>
                {viewsByModule.map(([module, count]) => (
                  <Flex key={module} gap={8} wrap>
                    <Text role="body" as="span">
                      {module}: {count}
                    </Text>
                    {moduleDrilldownPath(module) ? (
                      <Button
                        variant="ghost"
                        onClick={() =>
                          void navigate(moduleDrilldownPath(module)!)
                        }
                      >
                        Open module
                      </Button>
                    ) : null}
                  </Flex>
                ))}
              </Stack>
            )}
          </Card>
          <Card
            header={
              <Text role="title" as="h3">
                Breakdown: segments by classification (this page)
              </Text>
            }
          >
            {segmentsByClassification.length === 0 ? (
              <EmptyState
                title="No active segments on this page"
                reason="none"
              />
            ) : (
              <Stack gap={8}>
                <Text role="caption" color="muted" as="p">
                  Grouping of list results by classification. Not audience size
                  or engagement.
                </Text>
                {segmentsByClassification.map(([classification, count]) => (
                  <Text key={classification} role="body" as="p">
                    {formatLabel(classification)}: {count}
                  </Text>
                ))}
              </Stack>
            )}
          </Card>
        </Flex>
      ) : null}
    </Stack>
  );
}

function QueueShell({
  title,
  description,
  breadcrumbs,
  filters,
  error,
  loading,
  empty,
  children,
  page,
  nextCursor,
  onNext,
  onPrevious,
  onRefresh,
}: {
  title: string;
  description: string;
  breadcrumbs: { label: string; to?: string }[];
  filters: ReactNode;
  error: string | null;
  loading: boolean;
  empty: boolean;
  children: ReactNode;
  page: number;
  nextCursor: string | null;
  onNext: () => void;
  onPrevious: () => void;
  onRefresh: () => void;
}) {
  usePageBreadcrumbs(breadcrumbs);

  return (
    <Stack gap={24}>
      <SectionHeader
        title={title}
        description={description}
        actions={
          <Button variant="secondary" onClick={onRefresh}>
            Refresh
          </Button>
        }
      />
      <Filters>{filters}</Filters>
      {error ? (
        <Alert tone="danger" title="Load failed">
          {error}
        </Alert>
      ) : null}
      {loading ? <LoadingState message={`Loading ${title}…`} /> : null}
      {!loading && !error && empty ? (
        <EmptyState
          title={`No ${title.toLowerCase()}`}
          description="Adjust filters or create records via Admin/System when authorized."
          reason="none"
        />
      ) : null}
      {!loading && !empty ? (
        <Stack gap={12}>
          <ActionBar
            left={
              <Text role="small" color="secondary" as="span">
                Page {page}
              </Text>
            }
            right={
              <Pagination
                page={page}
                pageCount={nextCursor ? page + 1 : page}
                onPageChange={(nextPage) => {
                  if (nextPage > page) onNext();
                  if (nextPage < page) onPrevious();
                }}
              />
            }
          />
          {children}
        </Stack>
      ) : null}
    </Stack>
  );
}

export function SavedViewsQueuePage() {
  const navigate = useNavigate();
  const client = useApiClient();
  const api = useMemo(() => createAnalyticsApi(client), [client]);

  const loadPage = useMemo(
    () =>
      async (params: {
        cursor?: string;
        status: string;
        sort: string;
        extra: Record<string, string>;
      }) => {
        const list = await api.listSavedViews({
          ...(params.cursor ? { cursor: params.cursor } : {}),
          limit: 25,
          ...(params.status ? { status: params.status } : {}),
          ...(params.sort ? { sort: params.sort } : {}),
          ...(params.extra.module ? { module: params.extra.module } : {}),
          ...(params.extra.owner_user_id
            ? { owner_user_id: params.extra.owner_user_id }
            : {}),
        });
        return {
          items: list.items,
          nextCursor: list.next_cursor ?? null,
        };
      },
    [api],
  );

  const queue = useAnalyticsQueue<SavedViewDto>(loadPage, {
    sort: "-created_at",
  });

  return (
    <QueueShell
      title="Saved views"
      description="Read-only list of saved_view records. filter_definition and sort_definition are opaque JSON stored for a module — HQ Analytics does not execute them as queries or invent metrics."
      breadcrumbs={[
        { label: "HUDDL HQ", to: "/app" },
        { label: "Analytics", to: "/app/analytics" },
        { label: "Saved views" },
      ]}
      filters={
        <Flex gap={12} wrap>
          <Select
            label="Status"
            value={queue.status}
            onChange={(event) => queue.setStatus(event.target.value)}
            options={[
              { value: "", label: "All statuses" },
              ...RECORD_STATUSES.map((value) => ({
                value,
                label: formatLabel(value),
              })),
            ]}
          />
          <Input
            label="Module"
            value={queue.extra.module ?? ""}
            onChange={(event) => queue.setExtra("module", event.target.value)}
            hint="Exact module string stored on the saved view."
          />
          <Input
            label="Owner User ID"
            value={queue.extra.owner_user_id ?? ""}
            onChange={(event) =>
              queue.setExtra("owner_user_id", event.target.value)
            }
          />
          <Select
            label="Sort"
            value={queue.sort}
            onChange={(event) => queue.setSort(event.target.value)}
            options={[
              { value: "-created_at", label: "Created (newest)" },
              { value: "created_at", label: "Created (oldest)" },
              { value: "name", label: "Name" },
              { value: "id", label: "ID" },
            ]}
          />
          <Button variant="secondary" onClick={() => queue.clearFilters()}>
            Reset
          </Button>
        </Flex>
      }
      error={queue.error}
      loading={queue.loading}
      empty={queue.rows.length === 0}
      page={queue.page}
      nextCursor={queue.nextCursor}
      onNext={() => queue.goNext()}
      onPrevious={() => queue.goPrevious()}
      onRefresh={() => void queue.load()}
    >
      <Alert tone="info" title="Time scope">
        Sort uses created_at on the saved_view row. There is no metric
        date-range query on this endpoint.
      </Alert>
      <Table>
        <Thead>
          <Tr>
            <Th>Name</Th>
            <Th>Module</Th>
            <Th>Status</Th>
            <Th>Owner</Th>
            <Th>Created</Th>
          </Tr>
        </Thead>
        <Tbody>
          {queue.rows.map((row) => (
            <Tr
              key={row.id}
              onClick={() =>
                void navigate(`/app/analytics/saved-views/${row.id}`)
              }
            >
              <Td>{row.name}</Td>
              <Td>{row.module}</Td>
              <Td>
                <StatusChip tone={recordStatusTone(row.status)}>
                  {formatLabel(row.status)}
                </StatusChip>
              </Td>
              <Td>{row.owner_user_id}</Td>
              <Td>{row.created_at}</Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
    </QueueShell>
  );
}

export function SegmentsQueuePage() {
  const navigate = useNavigate();
  const client = useApiClient();
  const api = useMemo(() => createAnalyticsApi(client), [client]);

  const loadPage = useMemo(
    () =>
      async (params: {
        cursor?: string;
        status: string;
        sort: string;
        extra: Record<string, string>;
      }) => {
        const list = await api.listUserSegments({
          ...(params.cursor ? { cursor: params.cursor } : {}),
          limit: 25,
          ...(params.status ? { status: params.status } : {}),
          ...(params.sort ? { sort: params.sort } : {}),
          ...(params.extra.classification
            ? { classification: params.extra.classification }
            : {}),
          ...(params.extra.owner_user_id
            ? { owner_user_id: params.extra.owner_user_id }
            : {}),
        });
        return {
          items: list.items,
          nextCursor: list.next_cursor ?? null,
        };
      },
    [api],
  );

  const queue = useAnalyticsQueue<UserSegmentDto>(loadPage, {
    sort: "-created_at",
  });

  return (
    <QueueShell
      title="User segments"
      description="Read-only list of user_segment records. criteria_definition is opaque JSON — not expanded into membership counts or attribution."
      breadcrumbs={[
        { label: "HUDDL HQ", to: "/app" },
        { label: "Analytics", to: "/app/analytics" },
        { label: "Segments" },
      ]}
      filters={
        <Flex gap={12} wrap>
          <Select
            label="Status"
            value={queue.status}
            onChange={(event) => queue.setStatus(event.target.value)}
            options={[
              { value: "", label: "All statuses" },
              ...RECORD_STATUSES.map((value) => ({
                value,
                label: formatLabel(value),
              })),
            ]}
          />
          <Select
            label="Classification"
            value={queue.extra.classification ?? ""}
            onChange={(event) =>
              queue.setExtra("classification", event.target.value)
            }
            options={[
              { value: "", label: "All classifications" },
              ...CLASSIFICATIONS.map((value) => ({
                value,
                label: formatLabel(value),
              })),
            ]}
          />
          <Input
            label="Owner User ID"
            value={queue.extra.owner_user_id ?? ""}
            onChange={(event) =>
              queue.setExtra("owner_user_id", event.target.value)
            }
          />
          <Select
            label="Sort"
            value={queue.sort}
            onChange={(event) => queue.setSort(event.target.value)}
            options={[
              { value: "-created_at", label: "Created (newest)" },
              { value: "created_at", label: "Created (oldest)" },
              { value: "name", label: "Name" },
              { value: "id", label: "ID" },
            ]}
          />
          <Button variant="secondary" onClick={() => queue.clearFilters()}>
            Reset
          </Button>
        </Flex>
      }
      error={queue.error}
      loading={queue.loading}
      empty={queue.rows.length === 0}
      page={queue.page}
      nextCursor={queue.nextCursor}
      onNext={() => queue.goNext()}
      onPrevious={() => queue.goPrevious()}
      onRefresh={() => void queue.load()}
    >
      <Alert tone="info" title="Time scope">
        Sort uses created_at on the user_segment row. Segment evaluation and
        membership expansion are not available on this endpoint.
      </Alert>
      <Table>
        <Thead>
          <Tr>
            <Th>Name</Th>
            <Th>Status</Th>
            <Th>Classification</Th>
            <Th>Owner</Th>
            <Th>Created</Th>
          </Tr>
        </Thead>
        <Tbody>
          {queue.rows.map((row) => (
            <Tr
              key={row.id}
              onClick={() =>
                void navigate(`/app/analytics/segments/${row.id}`)
              }
            >
              <Td>{row.name}</Td>
              <Td>
                <StatusChip tone={recordStatusTone(row.status)}>
                  {formatLabel(row.status)}
                </StatusChip>
              </Td>
              <Td>
                <StatusChip tone={classificationTone(row.classification)}>
                  {formatLabel(row.classification)}
                </StatusChip>
              </Td>
              <Td>{row.owner_user_id}</Td>
              <Td>{row.created_at}</Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
    </QueueShell>
  );
}
